// Group by on the main board (None / Project / Person): the toolbar's select, and the heading over
// each group. A heading folds its items away (remembered per person, and in the URL: grouping.ts).
// A project's heading has its progress and the ⧉ ↗ its rows have elsewhere; a person's their avatar
// and how many open items they have here. The slot after a project's heading marks it like a card,
// so a panel opened from its ⧉ draws its bridge from the heading.

import { projectCode, type Viewer } from "@boem/core/shared";
import { GROUP_BY, GROUP_LABEL, projectTone, type GroupBy, type ItemGroup } from "../grouping.ts";
import { NobodyAvatar, PersonAvatar } from "./Assign.tsx";
import { Icon } from "./Icons.tsx";
import { Progress } from "./Structure.tsx";

/** The toolbar's Group by: a compact select (under 1024px it moves into the Filters popover, like the sort). */
export function GroupSelect({ value, onChange }: { value: GroupBy; onChange: (g: GroupBy) => void }) {
  return (
    <select className="chip group-select" aria-label="Group by" title="Group by: none, project or person" value={value} onChange={(e) => onChange(e.target.value as GroupBy)}>
      {GROUP_BY.map((g) => (
        <option key={g} value={g}>
          {g === "none" ? "No groups" : `By ${GROUP_LABEL[g].toLowerCase()}`}
        </option>
      ))}
    </select>
  );
}

export function GroupHead({
  group: g,
  folded,
  onFold,
  viewer,
  onPanel,
  onGoto,
  id,
}: {
  group: ItemGroup;
  folded: boolean;
  onFold: () => void;
  viewer: Pick<Viewer, "name">;
  /** ⧉: the project in a panel right of the board */
  onPanel?: (num: number) => void;
  /** ↗: the project's own board */
  onGoto?: (num: number) => void;
  /** the id of the list it folds */
  id: string;
}) {
  const p = g.project;
  const code = p ? (p.projectNo != null ? projectCode(p.projectNo) : `#${p.num}`) : null;
  const shown = `${g.items.length} item${g.items.length === 1 ? "" : "s"} here`;
  return (
    <div className="grp-head" data-by={g.by} data-key={g.key} data-folded={folded ? "1" : undefined}>
      <h2 className="grp-name">
        <button type="button" className="grp-fold" aria-expanded={!folded} aria-controls={id} title={`${folded ? "Show" : "Fold away"} ${g.label} (${shown})${g.shared ? `. ${g.shared === 1 ? "1 of them is" : `${g.shared} of them are`} with someone else too: an item with several people shows under each of them` : ""}`} onClick={onFold}>
          <Icon name="chevron" />
          {g.by === "project" ? (
            p ? (
              <>
                <b className="grp-code" data-tone={projectTone(p)}>
                  {code}
                </b>
                <span className="grp-title">{p.title}</span>
              </>
            ) : (
              <span className="grp-title grp-none">No project</span>
            )
          ) : (
            <>
              {g.person ? <PersonAvatar name={g.person} me={g.person === viewer.name} /> : <NobodyAvatar />}
              <span className={`grp-title${g.person ? "" : " grp-none"}`}>
                {g.label}
                {g.person === viewer.name ? " (me)" : ""}
              </span>
            </>
          )}
        </button>
      </h2>
      <span className="grp-meta">
        {g.by === "project" ? (
          <>
            {p && p.children.total > 0 && <Progress it={p} />}
            <span className="grp-n grp-shown">{shown}</span>
          </>
        ) : (
          <>
            <span className="grp-n">{g.open} open</span>
            {g.items.length > g.open && <span className="grp-n grp-shown">{g.items.length - g.open} closed</span>}
          </>
        )}
      </span>
      {p && (onPanel || onGoto) && (
        <span className="grp-acts">
          {onPanel && (
            <button type="button" className="sub-act" aria-label={`Open project ${code} ${p.title} in a panel`} title="Open in a panel to the right" onClick={() => onPanel(p.num)}>
              <Icon name="panel" />
            </button>
          )}
          {onGoto && (
            <button type="button" className="sub-act" aria-label={`Go to project ${code}'s board`} title={`Open ${p.title} as the board`} onClick={() => onGoto(p.num)}>
              <Icon name="goto" />
            </button>
          )}
        </span>
      )}
    </div>
  );
}
